import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { ADJUSTMENT_YEARS } from './forecastAdjustments';
import NumericInput from './NumericInput';

// Individual hire cohorts: one row per hire decision, timed by month, with
// per-row assumption overrides and an expandable per-year production editor.

const monthOptions = Array.from({ length: 60 }, (_, m) => ({
  value: m + 1,
  label: `FY${2027 + Math.floor(m / 12)} M${String((m % 12) + 1).padStart(2, '0')}`
}));

const ASSUMPTION_FIELDS = [
  { key: 'annualLoan', label: 'Loan prod $MM/yr', perYear: true },
  { key: 'annualDeposit', label: 'Deposit prod $MM/yr', perYear: true },
  { key: 'annualFee', label: 'Fees $MM/yr', perYear: true },
  { key: 'annualExpense', label: 'Comp+exp $MM/yr', perYear: false },
  { key: 'rampMonths', label: 'Ramp months', perYear: false }
];

// Per-officer risk overrides (default = role-level matrix values below).
const OFFICER_OVERRIDE_FIELDS = [
  { key: 'vacancyOverride', label: 'Vacancy %' },
  { key: 'attritionOverride', label: 'Attrition %' }
];

const ForecastHireRows = ({ hireRows, officerTypes, onChange, disabled }) => {
  const [expanded, setExpanded] = useState({});

  const update = (rowId, patch, label) => {
    onChange(hireRows.map((row) => (row.id === rowId ? { ...row, ...patch } : row)), label, rowId);
  };

  const addRow = (typeId) => {
    const type = officerTypes.find((t) => t.id === typeId) || officerTypes[0];
    const id = `hire-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const count = hireRows.filter((row) => row.typeId === type.id).length + 1;
    onChange([
      ...hireRows,
      { id, typeId: type.id, name: `${type.type.split(' - ')[0]} hire #${count}`, fte: 1, startMonth: 1, assumptions: {}, perYear: {} }
    ], `Added ${type.type} hire`, id);
  };

  // Split a multi-FTE row into individual 1-FTE hires so each officer carries
  // their own timing and assumptions.
  const splitRow = (row) => {
    const fte = Math.round(Number(row.fte) || 0);
    if (fte <= 1) return;
    const clones = Array.from({ length: fte }, (_, index) => ({
      ...row,
      id: `${row.id}-s${index + 1}`,
      name: `${row.name || 'Hire'} (${index + 1}/${fte})`,
      fte: 1,
      assumptions: { ...(row.assumptions || {}) },
      perYear: JSON.parse(JSON.stringify(row.perYear || {}))
    }));
    onChange(
      hireRows.flatMap((candidate) => (candidate.id === row.id ? clones : [candidate])),
      `Split ${fte}-FTE row into individual hires`,
      row.id
    );
    setExpanded((previous) => ({ ...previous, [row.id]: false }));
  };

  return (
    <div className="forecast-subpanel">
      <div className="forecast-panel-head">
        <span className="forecast-section-code">OP_04H · Hiring plan by individual hire</span>
        <h3>
          Every hire is its own row — name it, time it, and use the ▸ chevron for that hire's
          per-year production and risk overrides. Multi-FTE rows can be split (⫛) into individuals.
        </h3>
      </div>

      <div className="forecast-home-table-wrap">
        <table className="forecast-home-table hire-rows-table">
          <thead>
            <tr>
              <th /> <th>Role</th><th>FTE</th><th>Start</th>
              {ASSUMPTION_FIELDS.map((field) => <th key={field.key}>{field.label}</th>)}
              <th />
            </tr>
          </thead>
          <tbody>
            {hireRows.map((row) => {
              const type = officerTypes.find((t) => t.id === row.typeId) || officerTypes[0];
              const isOpen = expanded[row.id];
              return (
                <React.Fragment key={row.id}>
                  <tr>
                    <td>
                      <button
                        type="button" className="hire-expand" title="Per-year assumptions"
                        aria-label={`${isOpen ? 'Collapse' : 'Expand'} assumptions for ${row.name || type.type}`}
                        aria-expanded={isOpen}
                        onClick={() => setExpanded((prev) => ({ ...prev, [row.id]: !prev[row.id] }))}
                      >
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td>
                      <input
                        aria-label={`Name for ${type.type} hire`}
                        type="text"
                        className="hire-name"
                        placeholder={`${type.type.split(' - ')[0]} hire`}
                        value={row.name || ''}
                        disabled={disabled}
                        onChange={(event) => update(row.id, { name: event.target.value }, 'Hire name')}
                      />
                      <select
                        aria-label={`Role for ${row.name || 'new hire'}`}
                        value={row.typeId} disabled={disabled}
                        onChange={(event) => update(row.id, { typeId: event.target.value, assumptions: {}, perYear: {} }, 'Hire role')}
                      >
                        {officerTypes.map((t) => <option key={t.id} value={t.id}>{t.type}</option>)}
                      </select>
                    </td>
                    <td>
                      <NumericInput
                        ariaLabel={`FTE for ${row.name || type.type}`}
                        min={0} max={20} step={1}
                        value={row.fte} disabled={disabled}
                        onCommit={(value) => update(row.id, { fte: value }, 'Hire FTE')}
                      />
                    </td>
                    <td>
                      <select
                        aria-label={`Start month for ${row.name || type.type}`}
                        value={row.startMonth} disabled={disabled}
                        onChange={(event) => update(row.id, { startMonth: Number(event.target.value) }, 'Hire start month')}
                      >
                        {monthOptions.slice(0, 36).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </td>
                    {ASSUMPTION_FIELDS.map((field) => (
                      <td key={field.key}>
                        <NumericInput
                          ariaLabel={`${field.label} for ${row.name || type.type}`}
                          placeholder={String(type[field.key])}
                          value={row.assumptions?.[field.key] ?? ''}
                          disabled={disabled}
                          onCommit={(value) => {
                            const assumptions = { ...(row.assumptions || {}), [field.key]: value };
                            update(row.id, { assumptions }, `Hire ${field.label}`);
                          }}
                        />
                      </td>
                    ))}
                    <td>
                      {!disabled && (
                        <span className="hire-row-actions">
                          {Number(row.fte) > 1 && (
                            <button
                              type="button" className="hire-expand" title={`Split into ${Math.round(row.fte)} individual hires`}
                              aria-label={`Split ${row.name || type.type} into ${Math.round(row.fte)} individual hires`}
                              onClick={() => splitRow(row)}
                            >
                              ⫛
                            </button>
                          )}
                          <button
                            type="button" className="hire-expand danger" title="Remove hire"
                            aria-label={`Remove ${row.name || type.type}`}
                            onClick={() => onChange(hireRows.filter((r) => r.id !== row.id), 'Removed hire', row.id)}
                          >
                            <Trash2 size={13} />
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="hire-peryear-row">
                      <td />
                      <td colSpan={8}>
                        <div className="hire-peryear">
                          {ASSUMPTION_FIELDS.filter((f) => f.perYear).map((field) => (
                            <div className="hire-peryear-line" key={field.key}>
                              <span>{field.label} by year</span>
                              {ADJUSTMENT_YEARS.map((year, yearIndex) => (
                                <label key={year}>
                                  <small>FY{String(year).slice(2)}</small>
                                  <NumericInput
                                    ariaLabel={`${field.label} for ${row.name || type.type} FY${year}`}
                                    placeholder={String(row.assumptions?.[field.key] ?? type[field.key])}
                                    value={row.perYear?.[field.key]?.[yearIndex] ?? ''}
                                    disabled={disabled}
                                    onCommit={(value) => {
                                      const perYear = { ...(row.perYear || {}) };
                                      const arr = [...(perYear[field.key] || ADJUSTMENT_YEARS.map(() => null))];
                                      arr[yearIndex] = value;
                                      perYear[field.key] = arr;
                                      update(row.id, { perYear }, `Hire ${field.label} FY${year}`);
                                    }}
                                  />
                                </label>
                              ))}
                            </div>
                          ))}
                          <div className="hire-peryear-line">
                            <span>Officer risk overrides</span>
                            {OFFICER_OVERRIDE_FIELDS.map((field) => (
                              <label key={field.key}>
                                <small>{field.label}</small>
                                <NumericInput
                                  ariaLabel={`${field.label} for ${row.name || type.type}`}
                                  step={0.1} min={0} max={30}
                                  placeholder="role"
                                  value={row.assumptions?.[field.key] ?? ''}
                                  disabled={disabled}
                                  onCommit={(value) => {
                                    const assumptions = { ...(row.assumptions || {}), [field.key]: value };
                                    update(row.id, { assumptions }, `Hire ${field.label}`);
                                  }}
                                />
                              </label>
                            ))}
                          </div>
                          <p className="forecast-submit-note">
                            Blank cells inherit the row value (or the role default). Year values feed the
                            monthly production engine and OP_04B going-on analysis. Vacancy/attrition blank
                            = role-level matrix value.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {!disabled && (
        <div className="forecast-submit-chips" style={{ marginTop: 10 }}>
          {officerTypes.map((type) => (
            <button key={type.id} type="button" className="forecast-secondary-button compact" onClick={() => addRow(type.id)}>
              <Plus size={12} /> {type.type.split(' - ')[0].replace('Financial Advisor', 'FA')}{type.type.includes(' - ') ? ` · ${type.type.split(' - ')[1]}` : ''}
            </button>
          ))}
        </div>
      )}
      <p className="forecast-submit-note">
        Departures, vacancy, and attrition overrides remain role-level in the matrix below; cohort rows
        drive gross hiring, timing, and production economics.
      </p>
    </div>
  );
};

export default ForecastHireRows;
